<template>
    <label class="f fh fa-c" :class="[uiStyle ? 'el--t-' + uiStyle : '']" :for="uid" :title="tooltip && $options.filters.athT(tooltip)">
        <slot name="before"></slot>
        <div :class="[
            'el--s-' + size,
            checked ? 'el--active' : '',
            disabled ? 'el--disabled' : '',
            display === 'toggle' ? 'mead-toggle-checkbox-display' : '',
            display === 'check' ? 'mead-standard-checkbox-display' : ''
        ]">
            <input ref="input"
                type="checkbox"
                :name="uid"
                :id="uid"
                :value="checked"
                :disabled="disabled"
                @input.prevent.stop="update" />
            <span :class="['el-frm-' + display, intermediate ? 'intermediate' : '']" ref="toggle"></span>
        </div>
        <slot name="after"></slot>
        <span class="fg sm-l-s te-note" v-if="lbl && !lblEntity" v-html="$options.filters.athT(lbl)">
        </span>
        <span class="fg sm-l-s te-note" v-if="lbl && lblEntity" v-html="$options.filters.athTEnt(lbl, lblEntity, lblEntityLc)">
        </span>
    </label>
</template>

<script>
import { guuid } from '@aretheyhappy/utils';

export default {
    name: 'mead-form-checkbox',
    props: {
        /**
         * Label text (or i18n key) rendered next to the checkbox. Rendered via the `athT` filter, or
         * `athTEnt` when `lblEntity` is also set.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it via `athTEnt`.
         */
        lblEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc : {type: Boolean, default: () => true},
        /**
         * Visual style of the checkbox: 'check' (default box with checkmark) or 'toggle' (switch style).
         */
        display     : {type: String, default: () => 'check'},
        /**
         * Whether the checkbox should be displayed as partially checked (only applies when
         * `display` is 'check').
         */
        intermediate: {type: Boolean, default: () => false},
        /**
         * Checked state of the checkbox (v-model).
         */
        value       : {type: Boolean, default: () => false},
        /**
         * Disables the checkbox input and applies disabled styling.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * Size modifier applied to the checkbox visual (e.g. 's', 'm').
         */
        size        : {type: String, default: () => 'm'},
        /**
         * Optional style variant applied as a modifier class on the wrapping label (e.g. 'listel').
         */
        uiStyle     : {type: [Boolean, String], default: () => false},
        /**
         * Tooltip text (i18n key) shown as the `title` attribute on the wrapping label.
         */
        tooltip     : {type: [String], default: () => ''},
    },
    data () {
        return {
            checked : this.value,
            uid     : guuid(),
        };
    },
    watch: {
        value: function () {
            this.checked = this.value;
            this.$refs.input.checked = this.value;
        },
    },
    methods: {
        update: function () {
            this.checked = this.$refs.input.checked;
            this.$emit('input', this.checked);
        },
    },
    mounted () {
        if (this.value) {
            this.checked = this.value;
            this.$refs.input.checked = this.value;
        }
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

// Note: This block was migrated from webapp, not sure what its purpose is
label {
    &.el--t-listel {
        transition: background-color .2s ease;
        border-radius: $br-s;
        padding: $s-xs;

        &:hover {
            background-color: var(--c-brand-light-bg);
        }
    }
}

.mead-standard-checkbox-display {
    cursor: pointer;

    input[type="checkbox"] {
        display: none;
        opacity: 0;
    }

    .el-frm-check {
        display: block;
        width: 2rem;
        height: 2rem;
        line-height: 2rem;
        border: 1px solid var(--c-frm-border);
        background-color: var(--c-frm-panel-bg);
        border-radius: $br-s;
        position: relative;
        cursor: pointer;
        font-weight: 600;
        text-align: center;
        transition: border .15s ease-in-out, color .15s ease-in-out;

        &:after {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: inline-block;
            color: var(--c-txt);
            font-family: 'LinearIcons', sans-serif;
            content: "\e934";
            opacity: 0;
            font-size: $fs-xs;
        }

        &.intermediate:after {
            top: -.1rem;
            left: .1rem;
            content: "\e937";
        }
    }

    & + span {
        cursor: pointer;
    }

    //  MODIFIERS

    &.el--active .el-frm-check,
    input[type="checkbox"]:checked + .el-frm-check {
        background-color: var(--c-brand-active-bg);
        border-color: var(--c-brand-active-bg);

        &:after {
            color: var(--c-brand-active-fg);
            opacity: 1;
        }
    }

    // Used by select-all checkboxes that are partially selected so the dash
    // reads as neutral/default instead of the normal active brand color.
    label.el--t-partial-default &.el--active .el-frm-check,
    label.el--t-partial-default & input[type="checkbox"]:checked + .el-frm-check {
        background-color: var(--c-brand-default-bg);
        border-color: var(--c-brand-default-bg);

        &:after {
            color: var(--c-brand-default-fg);
        }
    }

    &.el--disabled + span {
        cursor: not-allowed;
    }

    &.el--disabled .el-frm-check,
    input[type="checkbox"]:disabled + .el-frm-check {
        opacity: .65;
        filter: grayscale(1);
        cursor: not-allowed !important;
    }

    //  SIZES

    @mixin elFrmCheckSize ($name, $dim, $br, $fs) {
        &.el--s-#{$name} {
            .el-frm-check {
                width: $dim;
                height: $dim;
                line-height: $dim;
                border-radius: $br;

                &:after {
                    font-size: $fs;
                }
            }
        }
    }

    @include elFrmCheckSize('s', 2rem, $br-s, $fs-xs);
    @include elFrmCheckSize('m', 3rem, $br-m, $fs-l);
}

// Toggle
.mead-toggle-checkbox-display {
    display: inline-block;

    input[type="checkbox"] {
        display: none;
        opacity: 0;

        &:checked + .el-frm-toggle {
            padding: $s-xs $s-xs $s-xs $s-l;
            background-color: var(--c-brand-active-bg);
        }

        &[disabled] + .el-frm-toggle {
            opacity: .65;
            filter: grayscale(1);
            cursor: not-allowed !important;
        }
    }

    .el-frm-toggle {
        display: inline-block;
        cursor: pointer;
        padding: $s-xs $s-l $s-xs $s-xs;
        transition: all ease .2s;
        border-radius: $br-m;
        background-color: var(--c-frm-border);

        &:before {
            display: block;
            width: 12px;
            height: 12px;
            content: '';
            border-radius: 50%;
            background: var(--c-panel-bg);
        }
    }
}
</style>
